//! NOTE: This grid layout has to be used for modals
import React, { useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { LicenseManager } from "ag-grid-enterprise";
import { GridReadyEvent } from "ag-grid-community";
import {
    agDefaultSidebar,
    agDefaultColDef,
    overlayLoadingTemplate,
    overlayNoRowsTemplate,
} from "@ts/constants";
import {
    sortByStatusAndColumn,
    onModelUpdated,
    onRowDataChanged,
    isExternalFilterPresent,
    doesExternalFilterPass,
    initialFilters,
    resetActiveGridState
} from "@ts/utils";
import { GridLayoutTypes, IChannel, TActiveClientId } from "@ts/common-types";
import { useSelector } from "react-redux";

const AgGridWidget: React.FC<GridLayoutTypes> = React.memo(
    ({
        columnDefs,
        rowData,
        setGridApi,
        onSelectionChanged,
        pagination,
        isTotalRowsReq,
        isRowSelectable,
        isSortByStatusReq,
        setGridHeight,
        gridParams,
        rowHeight,
        api,
        loading
    }) => {
        process.env.VITE_NODE_ENV === "production" &&
            LicenseManager.setLicenseKey(process.env.VITE_AGGRID_LICENSE_KEY);
        const ref = useRef(null);
        const activeClientId = useSelector(
            (state: TActiveClientId) => state.activeClientId.value
        );
        const channel = useSelector((state: IChannel) => state.channel.value);

        const onGridReady = () => {
            setGridApi && setGridApi(ref.current.api);
            initialFilters(ref.current.api);
        };

        const onFirstDataRendered = (e: GridReadyEvent) => {
            isSortByStatusReq && sortByStatusAndColumn(e.columnApi);
            e.api.sizeColumnsToFit();
        };

        const calculateGridHeight = () => {
            const cardBody = document.querySelector(".ant-card-body");
            if (cardBody) {
                const modalBodyHeight = cardBody.clientHeight;
                const headerHeight = 15;
                return modalBodyHeight - headerHeight;
            }
            return 300;
        };

        const handleResize = () => {
            setGridHeight && setGridHeight(calculateGridHeight());
            ref.current.api?.sizeColumnsToFit();
        };

        // For resizing the widget grids
        useEffect(() => {
            handleResize();
            window.addEventListener("resize", handleResize);
            return () => {
                window.removeEventListener("resize", handleResize);
            };
            // eslint-disable-next-line
        }, []);

        // For resetting current grid state on client and channel change
        useEffect(() => {
            if (ref.current.api) {
                resetActiveGridState(ref.current.api);
            }
        }, [activeClientId, channel]);

        //added loading logic to widget layouts
        useEffect(() => {
            if (api) {
                if (loading) {
                    api.showLoadingOverlay();
                } else {
                    if (rowData && rowData.length === 0) {
                        api.showNoRowsOverlay();
                    } else {
                        api.hideOverlay();
                    }
                }
            }
        }, [loading, rowData, api]);

        const gridProps = {
            ref,
            onGridReady,
            rowData,
            columnDefs,
            overlayLoadingTemplate,
            overlayNoRowsTemplate,
            onFirstDataRendered,
            pagination,
            onSelectionChanged,
            onRowDataChanged,
            isExternalFilterPresent,
            doesExternalFilterPass,
            isRowSelectable,
            gridParams
        };

        return (
            <>
                <AgGridReact
                    {...gridProps}
                    sideBar={agDefaultSidebar}
                    defaultColDef={agDefaultColDef}
                    animateRows={true}
                    suppressRowClickSelection={true}
                    rowSelection="multiple"
                    onModelUpdated={isTotalRowsReq && onModelUpdated}
                    rowHeight={rowHeight || gridParams?.rowHeight}
                ></AgGridReact>
            </>
        );
    }
);

export default AgGridWidget;
